import { AgGridTable } from '@components/AgGrid/AgGridTable';
import { defaultColDef } from '@components/AgGrid/AgGridTable/helpers';
import type { ITabItem } from '@components/Form';
import { TableTopPanel } from '@components/TableTopPanel';
import { TooltipWrapper } from '@components/TooltipWrapper';
import { RequestStatus } from '@features/request/requestSlice';
import type { ConstructionTypeData } from '@gql/graphql';
import { CalculationStatusEnum } from '@gql/graphql';
import { useAccess } from '@hooks/useAccess';
import { useProjectData } from '@hooks/useProjectData';
import { useProjectParams } from '@hooks/useProjectParams';
import { cnMixSpace, IconAlert, IconInfo, Informer, Layout, Loader, Tabs } from '@uikit';
import { PageHeaderLayoutContext } from 'pages/Project/Common/Layouts/PageLayoutContext';
import { ProjectPageLayout } from 'pages/Project/Common/Layouts/ProjectPageLayout';
import { NavItems } from 'pages/Project/Common/ProjectTree/constants';
import { isCostRoundingOrSeasonsChanged } from 'pages/Project/Common/utils';

import type { AgGridReact } from 'ag-grid-react';
import type { CSSProperties, FC } from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useBimCalculationSeasons } from '../BimCalculationOthers/hooks/useBimCalculationSeasons';
import { useBimcCalculation } from '../hooks/useBimcCalculation';

import { CreatingTypes, tableEmptyDataText } from './commonData';
import { BimCalculationLimitedEditModal } from './components/BimCalculationLimitedEditModal';
import { BimCalculationLimitedToolbar } from './components/BimCalculationLimitedToolbar';
import { useBimCalculationLimitedCosts } from './hooks/useBimCalculationLimitedCosts';
import { useBimCalculationLimitedCostsColumns } from './hooks/useBimCalculationLimitedCostsColumns';
import { useBimCalculationLimitedCostsGrouped } from './hooks/useBimCalculationLimitedCostsGrouped';

import Styles from './BimCalculationLimited.module.css';

const xl = cnMixSpace({
    mB: 'xl',
});

const title = 'Доп.транспорт и лимитированные затраты';

const calculationLimitedContext = {
    tableUniqueId: 'bimCalculationLimitedContext',
};

export const BimCalculationLimited: FC = () => {
    const { id: projectId, methodology } = useProjectParams();

    const { projectData } = useProjectData(projectId);

    const calculationId = projectData?.calculationNavigation?.data?.calculationNavData?.id;
    const { calculationLimited: calculationLimitedRules } = useAccess();

    const { data: calculationData, status: calculationDataStatus } = useBimcCalculation(calculationId);

    const tableRef = useRef<AgGridReact | null>(null);
    const tableApi = useMemo(() => (tableRef && 'current' in tableRef ? tableRef.current : undefined), [tableRef]);
    const tableTopPanelRef = useRef<HTMLDivElement>(null);

    const { isHeaderOpened } = useContext(PageHeaderLayoutContext);

    const [position, setPosition] = useState<number | undefined>();
    const [activeTab, setActiveTab] = useState<ITabItem>();
    const [createOpened, setCreateOpened] = useState<
        | {
              type?: CreatingTypes;
              isAdditionalTransport?: boolean;
          }
        | boolean
    >(false);

    const breadCrumbsItems = useMemo(
        () => [
            {
                label: projectData?.name,
                href: `/projects/${methodology}/${projectData?.uniqueId}/`,
            },
            {
                label: 'Расчет НМЦ',
                href: `/projects/${methodology}/${projectData?.uniqueId}/calculation`,
            },
        ],
        [methodology, projectData?.uniqueId, projectData?.name],
    );

    const calculationConstructionTypes = useMemo<ConstructionTypeData[]>(
        () =>
            (calculationData?.constructionTypes ?? []).map(
                (constructionType) =>
                    ({
                        ...constructionType.constructionType,
                        id: constructionType.constructionType?.id ?? constructionType.id,
                        name: constructionType.constructionType?.name ?? constructionType.name,
                    }) as ConstructionTypeData,
            ),
        [calculationData?.constructionTypes],
    );

    const sortedConstructionTypesTabsItems = useMemo(
        () =>
            calculationConstructionTypes
                ?.map((t: ConstructionTypeData) => ({ id: t.id, label: t.name }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [calculationConstructionTypes],
    );

    useEffect(() => {
        if (tableTopPanelRef.current) {
            setPosition(tableTopPanelRef.current?.getBoundingClientRect().bottom);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableTopPanelRef.current?.getBoundingClientRect().bottom, isHeaderOpened]);

    const isCalculationCompleted = useMemo(
        () => CalculationStatusEnum.Completed === calculationData?.status,
        [calculationData?.status],
    );

    const hasCalculationExecuted = useMemo(
        () => Boolean(calculationData?.lastComputedAt),
        [calculationData?.lastComputedAt],
    );

    const isCreateDisabled = useMemo(
        () => isCalculationCompleted || !activeTab?.id || !calculationLimitedRules.canCreate,
        [activeTab?.id, isCalculationCompleted, calculationLimitedRules.canCreate],
    );

    const [seasons] = useBimCalculationSeasons(calculationId);

    const { data, calculationLimitedCostsStatus, doReload, changeFilterHandler } = useBimCalculationLimitedCosts({
        calculationId,
        constructionTypeId: activeTab?.id as number,
    });

    const costRounding = useMemo(() => calculationData?.costRounding || 0, [calculationData?.costRounding]);

    const { columns, fullSetSettingsColumns, isColumnVisible, tableSettingsConfirm } =
        useBimCalculationLimitedCostsColumns({
            seasons,
            costRounding,
            constructionTypes: calculationConstructionTypes,
            calculationId,
            constructionTypeId: activeTab?.id as number,
            onOperationSuccess: doReload,
            changeFilterHandler,
            isCalculationCompleted,
        });

    const { rowsGrouped, itemsCount } = useBimCalculationLimitedCostsGrouped({
        items: data,
        constructionTypeId: activeTab?.id as number,
    });

    const onTabChange = useCallback(
        ({ value }) => {
            setActiveTab(value);
        },
        [setActiveTab],
    );

    const onCreateClick = useCallback((creatingType?: CreatingTypes, isAdditionalTransport?: boolean) => {
        setCreateOpened({
            type: creatingType,
            isAdditionalTransport,
        });
    }, []);

    const onCreateClose = useCallback(() => {
        setCreateOpened(false);
    }, []);

    const creatingTypeProp = useMemo(() => {
        if (typeof createOpened === 'object' && 'type' in createOpened) {
            return createOpened.type ?? CreatingTypes.MANUAL;
        }

        return CreatingTypes.MANUAL;
    }, [createOpened]);

    const isAdditionalTransportProp = useMemo(() => {
        if (typeof createOpened === 'object' && 'isAdditionalTransport' in createOpened) {
            return createOpened.isAdditionalTransport ?? false;
        }

        return false;
    }, [createOpened]);

    useEffect(() => {
        if (sortedConstructionTypesTabsItems?.length) {
            setActiveTab(sortedConstructionTypesTabsItems[0]);
        }
    }, [sortedConstructionTypesTabsItems]);

    const { isSeasonsChanged } = useMemo(
        () =>
            isCostRoundingOrSeasonsChanged(
                NavItems.CalculationLimitedCosts,
                projectData?.calculationNavigation?.data?.calculationNavData,
            ),
        [projectData?.calculationNavigation?.data?.calculationNavData],
    );

    return (
        <ProjectPageLayout
            title={title}
            breadCrumbsItems={breadCrumbsItems}
            isHeaderCollapsable
            headerBottom={
                <div className="wrapperToolbarLine limitedToolbarLine">
                    <BimCalculationLimitedToolbar isDisabled={isCreateDisabled} onCreateClick={onCreateClick} />
                    {hasCalculationExecuted && (
                        <Informer className={Styles.informer} status="system" size="s" icon={IconInfo}>
                            <div style={{ marginBottom: '2px' }}>
                                В случае редактирования лимитированных затрат и доп. транспорта после формирования
                                расчета НМЦ для отображения актуальных данных необходимо повторно сформировать расчет
                            </div>
                        </Informer>
                    )}
                </div>
            }
            classNameHeader={Styles.calculationLimitedHeader}
            headerLeftSide={
                isSeasonsChanged && (
                    <TooltipWrapper
                        direction="upCenter"
                        isHidden={false}
                        message="Сезоны в карточке расчета НМЦ были изменены"
                        status="warning"
                        style={{ marginRight: '15px', height: '14px', width: '14px' }}
                    >
                        <IconAlert size="s" view="warning" />
                    </TooltipWrapper>
                )
            }
        >
            <Layout direction="column">
                <>
                    {createOpened && activeTab?.id && (
                        <Layout className={xl}>
                            <BimCalculationLimitedEditModal
                                handleClose={onCreateClose}
                                creatingType={creatingTypeProp}
                                isAdditionalTransportProp={isAdditionalTransportProp}
                                constructionTypeId={activeTab?.id}
                                reloadHandler={doReload}
                                constructionTypes={calculationConstructionTypes}
                                calculationId={calculationId}
                                isCreateModal
                            />
                        </Layout>
                    )}
                    <Layout className={xl}>
                        <Tabs
                            value={activeTab}
                            onChange={onTabChange}
                            items={sortedConstructionTypesTabsItems || []}
                            getItemLabel={(item: { label: string | number }) => item.label}
                            view="bordered"
                        />
                    </Layout>
                    {calculationDataStatus === RequestStatus.FETCHING ? (
                        <Loader />
                    ) : (
                        <>
                            <TableTopPanel
                                totalItems={itemsCount}
                                fetchedItems={itemsCount}
                                optionalColumns={fullSetSettingsColumns}
                                isColumnVisible={isColumnVisible}
                                handleTableSettingsConfirm={tableSettingsConfirm}
                                showPinColumn
                                tableApi={tableApi}
                                ref={tableTopPanelRef}
                            />
                            <div
                                className={Styles.calculationLimitedTable}
                                style={
                                    {
                                        '--tableStartPosition': `${position ?? 0}px`,
                                    } as CSSProperties
                                }
                            >
                                <AgGridTable
                                    loading={calculationLimitedCostsStatus === RequestStatus.FETCHING}
                                    rowData={rowsGrouped}
                                    columnDefs={columns}
                                    animateRows
                                    overlayNoRowsTemplate={tableEmptyDataText}
                                    defaultColDef={defaultColDef}
                                    ref={tableRef}
                                    context={calculationLimitedContext}
                                />
                            </div>
                        </>
                    )}
                </>
            </Layout>
        </ProjectPageLayout>
    );
};

BimCalculationLimited.displayName = 'BimCalculationLimited';
